import { path } from '../../../visualizations/common/path.js';
import { vector } from '../../../visualizations/common/types.js';
import { distance } from '../../common/vector.js';
import { bounds } from '../types.js';
import { calculateCatmullBezierCurves, fitBezierCurveToBoundary } from './catmull.js';
import { clampedYInterpolation } from './interpolation.js';
export const getStraightAreaPathData = (line: vector[], boundary: bounds, domainPosition: number) => {
  const currentPath = getStraightLinePathData(line, boundary, false);
  if (currentPath.isEmpty()) {
    return currentPath;
  }
  const startPoint = line[0];
  const endPoint = line[line.length - 1];
  currentPath.lineTo(endPoint[0], Math.abs(endPoint[1] - boundary.top) < Math.abs(endPoint[1] - boundary.bottom) ? boundary.top : boundary.bottom);
  currentPath.lineTo(endPoint[0], domainPosition);
  currentPath.lineTo(startPoint[0], domainPosition);
  currentPath.lineTo(startPoint[0], Math.abs(startPoint[1] - boundary.top) < Math.abs(startPoint[1] - boundary.bottom) ? boundary.top : boundary.bottom);
  return currentPath;
};
export const getStraightLinePathData = (line: vector[], boundary: bounds, jump = true) => {
  const currentPath = path();
  if (line.length < 2) {
    return currentPath;
  }
  for (let i = 0; i < line.length - 1; i++) {
    const [cx, cy] = line[i];
    const [nx, ny] = line[i + 1];
    const [x0, y0] = clampedYInterpolation(cx, cy, nx, ny, boundary);
    const [x1, y1] = clampedYInterpolation(nx, ny, cx, cy, boundary);
    if (i === 0 || currentPath.isEmpty()) {
      currentPath.moveTo(x0, y0);
    } else if (cx !== x0) {
      if (jump) {
        currentPath.moveTo(x0, y0);
      } else {
        currentPath.lineTo(x0, y0);
      }
    }
    const epsilon = 0.001;
    const isNearNextPosition = distance(x0, y0, x1, y1) < epsilon;
    if (!isNearNextPosition) {
      currentPath.lineTo(x1, y1);
    }
  }
  return currentPath;
};
export const getCurvedAreaPathData = (line: vector[], boundary: bounds, domainPosition: number) => {
  const currentPath = getCurvedLinePathData(line, boundary, false);
  const startPoint = line[1];
  const endPoint = line[line.length - 2];
  const initialX = endPoint[0];
  const initialY = Math.abs(endPoint[1] - boundary.top) < Math.abs(endPoint[1] - boundary.bottom) ? boundary.top : boundary.bottom;
  if (currentPath.isEmpty()) {
    currentPath.moveTo(initialX, initialY);
  } else {
    currentPath.lineTo(initialX, initialY);
  }
  currentPath.lineTo(endPoint[0], domainPosition);
  currentPath.lineTo(startPoint[0], domainPosition);
  currentPath.lineTo(startPoint[0], Math.abs(startPoint[1] - boundary.top) < Math.abs(startPoint[1] - boundary.bottom) ? boundary.top : boundary.bottom);
  return currentPath;
};
export const getCurvedLinePathData = (line: vector[], boundary: bounds, jump = true) => {
  const currentPath = path();
  if (line.length < 2) {
    return currentPath;
  }
  const curves = calculateCatmullBezierCurves(line, 0.5, 0);
  for (let i = 0; i < curves.length; i++) {
    const curve = fitBezierCurveToBoundary(curves[i], boundary);
    if (curve) {
      if (i === 0 || currentPath.isEmpty()) {
        currentPath.moveTo(curve[0][0], curve[0][1]);
      } else if (curves[i - 1][3][0] !== curve[0][0]) {
        if (jump) {
          currentPath.moveTo(curve[0][0], curve[0][1]);
        } else {
          currentPath.lineTo(curve[0][0], curve[0][1]);
        }
      }
      currentPath.cubicBezierTo(curve[1][0], curve[1][1], curve[2][0], curve[2][1], curve[3][0], curve[3][1]);
    }
  }
  return currentPath;
};